// SEO Constants and Configuration
export const SEO_CONFIG = {
  defaultTitle: "WebDoko Reading Materials - BSc CSIT, BCA, BIM, MCA Resources",
  titleTemplate: `%s | ${import.meta.env.VITE_SITE_NAME || "WebDoko Resources"}`,
  defaultDescription:
    import.meta.env.VITE_SITE_DESCRIPTION ||
    "Comprehensive collection of books, syllabus, reading materials and self-assessment tools for Nepal university programs including BSc. CSIT, BCA, BIM, MCA, MIT, MBS from TU, KU, PU.",
  siteUrl: import.meta.env.VITE_SITE_URL || "https://webdoko.com",
  siteName: import.meta.env.VITE_SITE_NAME || "WebDoko Resources",
  author: "WebDoko Resources Team",
  keywords: [
    "WebDoko",
    "BSc CSIT",
    "BCA",
    "BIM",
    "MCA",
    "MIT",
    "MBS",
    "TU syllabus",
    "KU books",
    "PU materials",
    "reading materials",
    "textbooks",
    "self assessment",
    "study materials",
  ],
  language: "en",
  type: "website",
  twitterHandle: import.meta.env.VITE_TWITTER_HANDLE || "@webdoko",
  facebookPage:
    import.meta.env.VITE_FACEBOOK_PAGE || "https://facebook.com/webdoko",
  organization: {
    name: import.meta.env.VITE_SITE_NAME || "WebDoko Resources",
    url: import.meta.env.VITE_SITE_URL || "https://webdoko.com",
    logo: `${import.meta.env.VITE_SITE_URL || "https://webdoko.com"}/logo.png`,
  },
};

// Page-specific SEO configurations
export const PAGE_SEO = {
  home: {
    title: "WebDoko Reading Materials & Resources",
    description:
      "Access comprehensive study materials, textbooks, syllabus, and self-assessment tools for Nepal university programs. Free resources for BSc CSIT, BCA, BIM, MCA students.",
    keywords: [
      "WebDoko resources",
      "free textbooks",
      "study materials",
      "BSc CSIT books",
      "BCA syllabus",
    ],
  },
  books: {
    title: "University Textbooks Collection",
    description:
      "Complete collection of textbooks for Nepal university programs. Download PDF books for BSc CSIT, BCA, BIM, MCA from leading publishers.",
    keywords: [
      "university textbooks",
      "Nepal books PDF",
      "BSc CSIT books",
      "BCA textbooks",
      "free download",
    ],
  },
  syllabus: {
    title: "University Courses & Syllabus Collection",
    description:
      "Explore complete course catalog and syllabus for Nepal university programs. Browse BSc CSIT, BCA, BIM, MCA, MIT, MBS courses from TU, KU, PU with detailed subject information.",
    keywords: [
      "university courses",
      "course catalog",
      "university syllabus",
      "TU syllabus",
      "KU curriculum",
      "PU courses",
      "Nepal education",
      "academic programs",
    ],
  },
  mcqs: {
    title: "Self Assessment",
    description:
      "Personalized self-assessment platform with customizable subjects, semesters, and difficulty levels for Nepal university students. Create tailored MCQ tests for BSc CSIT, BCA, BIM, MCA programs.",
    keywords: [
      "self assessment",
      "personalized testing",
      "adaptive learning",
      "exam preparation",
      "custom MCQs",
    ],
  },

  readingMaterial: {
    title: "Reading Materials & Study Guides",
    description:
      "Curated reading materials, study guides, and reference materials for Nepal university students.",
    keywords: [
      "reading materials",
      "study guides",
      "reference books",
      "academic resources",
    ],
  },
};

// Generate structured data for different content types
export const generateStructuredData = (type, data) => {
  const baseData = {
    "@context": "https://schema.org",
    "@type": type,
    publisher: {
      "@type": "Organization",
      name: SEO_CONFIG.organization.name,
      url: SEO_CONFIG.organization.url,
      logo: {
        "@type": "ImageObject",
        url: SEO_CONFIG.organization.logo,
      },
    },
  };

  switch (type) {
    case "Course":
      return {
        ...baseData,
        "@type": "Course",
        name: data.title,
        description: data.description,
        provider: {
          "@type": "Organization",
          name: data.university,
        },
        educationalCredentialAwarded: data.level,
        courseCode: data.code || data.id,
        numberOfCredits: data.credits,
        timeRequired: data.duration,
        coursePrerequisites: data.prerequisites,
        url: `${SEO_CONFIG.siteUrl}/syllabus/${data.slug}`,
        hasCourseInstance: {
          "@type": "CourseInstance",
          instructor: {
            "@type": "Person",
            name: data.instructor?.name || "University Faculty",
          },
        },
      };

    case "Quiz":

    case "Article":
      return {
        ...baseData,
        "@type": "Article",
        headline: data.title,
        description: data.description,
        author: {
          "@type": "Person",
          name: data.author,
        },
        datePublished: data.createdDate,
        dateModified: data.lastUpdated,
        wordCount: Math.ceil(data.readingTime * 200), // Approximate word count
        timeRequired: `PT${data.readingTime}M`,
        educationalLevel: data.level,
        url: `${SEO_CONFIG.siteUrl}/reading-material/${data.category}/${data.slug}`,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${SEO_CONFIG.siteUrl}/reading-material/${data.category}/${data.slug}`,
        },
      };

    case "WebSite":
      return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SEO_CONFIG.siteName,
        url: SEO_CONFIG.siteUrl,
        description: SEO_CONFIG.defaultDescription,
        inLanguage: SEO_CONFIG.language,
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SEO_CONFIG.siteUrl}/search?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      };

    case "Organization":
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SEO_CONFIG.organization.name,
        url: SEO_CONFIG.organization.url,
        logo: SEO_CONFIG.organization.logo,
        sameAs: [
          SEO_CONFIG.facebookPage,
          `https://twitter.com/${SEO_CONFIG.twitterHandle.replace("@", "")}`,
        ],
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          availableLanguage: ["English", "Nepali"],
        },
      };

    default:
      return baseData;
  }
};

// Generate breadcrumb structured data
export const generateBreadcrumbData = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${SEO_CONFIG.siteUrl}${crumb.url}`,
    })),
  };
};

// Generate meta tags for social media
export const generateSocialTags = (data) => {
  const defaultImage = `${SEO_CONFIG.siteUrl}/images/og-default.jpg`;

  return {
    // Open Graph
    "og:title": data.title || SEO_CONFIG.defaultTitle,
    "og:description": data.description || SEO_CONFIG.defaultDescription,
    "og:type": data.type || SEO_CONFIG.type,
    "og:url": data.url || SEO_CONFIG.siteUrl,
    "og:image": data.image || defaultImage,
    "og:image:width": "1200",
    "og:image:height": "630",
    "og:site_name": SEO_CONFIG.siteName,
    "og:locale": "en_US",

    // Twitter Card
    "twitter:card": "summary_large_image",
    "twitter:site": SEO_CONFIG.twitterHandle,
    "twitter:creator": SEO_CONFIG.twitterHandle,
    "twitter:title": data.title || SEO_CONFIG.defaultTitle,
    "twitter:description": data.description || SEO_CONFIG.defaultDescription,
    "twitter:image": data.image || defaultImage,

    // Additional meta tags
    "application-name": SEO_CONFIG.siteName,
    "apple-mobile-web-app-title": SEO_CONFIG.siteName,
    "format-detection": "telephone=no",
    "mobile-web-app-capable": "yes",
    "msapplication-TileColor": "#2563eb",
    "theme-color": "#2563eb",
  };
};

// Category and level mappings for better SEO
export const CATEGORY_SEO = {
  "bsc-csit": {
    name: "BSc. CSIT",
    description:
      "Bachelor of Science in Computer Science and Information Technology",
    keywords: [
      "BSc CSIT",
      "computer science",
      "information technology",
      "programming",
    ],
  },
  bca: {
    name: "BCA",
    description: "Bachelor of Computer Application",
    keywords: [
      "BCA",
      "computer application",
      "software development",
      "web development",
    ],
  },
  bim: {
    name: "BIM",
    description: "Bachelor in Information Management",
    keywords: [
      "BIM",
      "information management",
      "business management",
      "IT management",
    ],
  },
  mca: {
    name: "MCA",
    description: "Master of Computer Application",
    keywords: [
      "MCA",
      "master computer application",
      "advanced programming",
      "software engineering",
    ],
  },
  mit: {
    name: "MIT",
    description: "Master in Information Technology",
    keywords: [
      "MIT",
      "information technology",
      "IT management",
      "technology leadership",
    ],
  },
  mbs: {
    name: "MBS",
    description: "Master of Business Studies",
    keywords: [
      "MBS",
      "business studies",
      "management",
      "business administration",
    ],
  },
  be: {
    name: "BE",
    description: "Bachelor of Engineering",
    keywords: ["BE", "engineering", "technical education", "applied sciences"],
  },
  mbbs: {
    name: "MBBS",
    description: "Bachelor of Medicine and Bachelor of Surgery",
    keywords: ["MBBS", "medical education", "medicine", "healthcare"],
  },
};

export const UNIVERSITY_SEO = {
  "Tribhuvan University": {
    abbreviation: "TU",
    keywords: ["TU", "Tribhuvan University", "Nepal largest university"],
  },
  "Kathmandu University": {
    abbreviation: "KU",
    keywords: ["KU", "Kathmandu University", "private university Nepal"],
  },
  "Pokhara University": {
    abbreviation: "PU",
    keywords: ["PU", "Pokhara University", "western Nepal university"],
  },
  "Purbanchal University": {
    abbreviation: "PurU",
    keywords: ["PurU", "Purbanchal University", "eastern Nepal university"],
  },
};

// Enhanced structured data generators
export const generateFAQStructuredData = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
};

export const generateBreadcrumbStructuredData = (breadcrumbs) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: `${SEO_CONFIG.siteUrl}${crumb.url}`,
    })),
  };
};

export const generateVideoStructuredData = (videoData) => {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: videoData.title,
    description: videoData.description,
    thumbnailUrl: videoData.thumbnail,
    uploadDate: videoData.publishDate,
    duration: videoData.duration,
    embedUrl: videoData.embedUrl,
    contentUrl: videoData.videoUrl,
    publisher: {
      "@type": "Organization",
      name: SEO_CONFIG.organization.name,
      logo: {
        "@type": "ImageObject",
        url: SEO_CONFIG.organization.logo,
      },
    },
  };
};

export const generateEventStructuredData = (eventData) => {
  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: eventData.name,
    description: eventData.description,
    startDate: eventData.startDate,
    endDate: eventData.endDate,
    location: {
      "@type": "Place",
      name: eventData.location.name,
      address: eventData.location.address,
    },
    organizer: {
      "@type": "Organization",
      name: SEO_CONFIG.organization.name,
      url: SEO_CONFIG.siteUrl,
    },
  };
};

export const generateImageStructuredData = (imageData) => {
  return {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    contentUrl: imageData.url,
    license: imageData.license,
    acquireLicensePage: imageData.licensePage,
    creditText: imageData.credit,
    creator: {
      "@type": "Person",
      name: imageData.creator,
    },
  };
};

// SEO validation utilities
export const validateSEOData = (data) => {
  const issues = [];

  // Check title length
  if (!data.title || data.title.length < 30 || data.title.length > 60) {
    issues.push({
      type: "warning",
      field: "title",
      message: "Title should be between 30-60 characters",
    });
  }

  // Check description length
  if (
    !data.description ||
    data.description.length < 120 ||
    data.description.length > 160
  ) {
    issues.push({
      type: "warning",
      field: "description",
      message: "Description should be between 120-160 characters",
    });
  }

  // Check keywords
  if (!data.keywords || data.keywords.length === 0) {
    issues.push({
      type: "warning",
      field: "keywords",
      message: "Keywords are recommended for better SEO",
    });
  }

  // Check image alt text
  if (data.image && !data.imageAlt) {
    issues.push({
      type: "warning",
      field: "imageAlt",
      message: "Image alt text is missing",
    });
  }

  return {
    isValid: issues.filter((i) => i.type === "error").length === 0,
    issues,
  };
};

// Advanced SEO features
export const generateSchemaMarkup = (type, data, context = {}) => {
  const baseMarkup = {
    "@context": "https://schema.org",
    "@type": type,
    publisher: {
      "@type": "Organization",
      name: SEO_CONFIG.organization.name,
      url: SEO_CONFIG.organization.url,
      logo: {
        "@type": "ImageObject",
        url: SEO_CONFIG.organization.logo,
      },
    },
  };

  switch (type) {
    case "EducationalOrganization":
      return {
        ...baseMarkup,
        name: SEO_CONFIG.organization.name,
        url: SEO_CONFIG.siteUrl,
        sameAs: [
          SEO_CONFIG.facebookPage,
          `https://twitter.com/${SEO_CONFIG.twitterHandle.replace("@", "")}`,
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Educational Resources",
          itemListElement:
            data.programs?.map((program) => ({
              "@type": "EducationalOccupationalProgram",
              name: program.name,
              description: program.description,
              provider: {
                "@type": "EducationalOrganization",
                name: program.university,
              },
            })) || [],
        },
      };

    case "LearningResource":
      return {
        ...baseMarkup,
        name: data.title,
        description: data.description,
        learningResourceType: data.resourceType,
        educationalLevel: data.level,
        about: data.subject,
        inLanguage: "en",
        isAccessibleForFree: true,
        license: "https://creativecommons.org/licenses/by-sa/4.0/",
      };

    case "Dataset":
      return {
        ...baseMarkup,
        name: data.title,
        description: data.description,
        keywords: data.keywords?.join(", "),
        license: "https://creativecommons.org/licenses/by-sa/4.0/",
        creator: {
          "@type": "Organization",
          name: SEO_CONFIG.organization.name,
        },
      };

    default:
      return generateStructuredData(type, data);
  }
};

// Performance and accessibility SEO
export const generatePerformanceHints = () => {
  return {
    preconnect: [
      "https://fonts.googleapis.com",
      "https://fonts.gstatic.com",
      "https://www.google-analytics.com",
      "https://www.googletagmanager.com",
    ],
    prefetch: ["/syllabus", "/reading-material"],
    preload: [
      {
        href: "/fonts/main-font.woff2",
        as: "font",
        type: "font/woff2",
        crossorigin: "anonymous",
      },
    ],
  };
};

// Multi-language SEO support
export const generateHrefLangTags = (currentPath, languages = ["en"]) => {
  const hrefLangs = [];

  languages.forEach((lang) => {
    hrefLangs.push({
      rel: "alternate",
      hreflang: lang,
      href: `${SEO_CONFIG.siteUrl}${lang === "en" ? "" : `/${lang}`}${currentPath}`,
    });
  });

  // Add x-default
  hrefLangs.push({
    rel: "alternate",
    hreflang: "x-default",
    href: `${SEO_CONFIG.siteUrl}${currentPath}`,
  });

  return hrefLangs;
};
